import { useEffect, useState } from "react";
import { Star, MessageSquarePlus, BookOpen } from "lucide-react";
import { Reveal } from "./Reveal";
import { GBP_URL } from "../data";
import { useLang } from "../i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Stars = ({ n = 5, size = 15 }) => (
  <span className="flex gap-0.5" aria-label={`${n} out of 5 stars`}>
    {[1, 2, 3, 4, 5].map((i) => (
      <Star
        key={i}
        size={size}
        className={i <= Math.round(n) ? "fill-gold text-gold" : "fill-maroon/10 text-maroon/15"}
      />
    ))}
  </span>
);

const ReviewCard = ({ review, i }) => (
  <Reveal delay={i * 0.08}>
    <article
      className="flex h-full flex-col rounded-2xl border border-maroon/10 bg-white p-6 shadow-sm shadow-maroon/5"
      data-testid={`review-card-${i}`}
    >
      <div className="flex items-center gap-3">
        {review.authorPhoto ? (
          <img
            src={review.authorPhoto}
            alt=""
            className="h-10 w-10 rounded-full object-cover"
            loading="lazy"
            referrerPolicy="no-referrer"
          />
        ) : (
          <span className="flex h-10 w-10 items-center justify-center rounded-full bg-peacock font-display text-base font-bold text-cream">
            {(review.authorName || "G")[0].toUpperCase()}
          </span>
        )}
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold text-ink" data-testid={`review-author-${i}`}>
            {review.authorName}
          </p>
          <p className="text-xs text-stone2">{review.relativeTime}</p>
        </div>
        <img src="/images/platforms/google.svg" alt="Google review" className="ml-auto h-5 w-5" loading="lazy" />
      </div>
      <div className="mt-3">
        <Stars n={review.rating} />
      </div>
      {review.text && (
        <p className="mt-3 flex-1 text-sm leading-relaxed text-stone2" data-testid={`review-text-${i}`}>
          {review.text}
        </p>
      )}
    </article>
  </Reveal>
);

export const Reviews = () => {
  const { s } = useLang();
  const [data, setData] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    fetch(`${API}/reviews`)
      .then((r) => {
        if (!r.ok) throw new Error("unavailable");
        return r.json();
      })
      .then(setData)
      .catch(() => setFailed(true));
  }, []);

  const rating = data?.rating ?? 5.0;
  const count = data?.userRatingCount ?? 3;

  return (
    <section id="reviews" className="relative bg-cream py-24 sm:py-32" data-testid="reviews-section">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <Reveal>
          <p className="text-xs uppercase tracking-[0.25em] text-peacock font-semibold" data-testid="reviews-overline">
            {s.reviews.overline}
          </p>
          <div className="mt-4 flex flex-wrap items-end justify-between gap-8">
            <h2 className="font-display text-4xl sm:text-5xl font-bold text-maroon tracking-tight" data-testid="reviews-heading">
              {s.reviews.heading}
            </h2>
            <div className="flex items-center gap-4 rounded-2xl border border-maroon/10 bg-white px-6 py-4 shadow-sm" data-testid="reviews-rating-badge">
              <img src="/images/platforms/google.svg" alt="Google" className="h-8 w-8" loading="lazy" />
              <div>
                <p className="font-display text-3xl font-black leading-none text-ink">{rating.toFixed(1)}</p>
                <div className="mt-1 flex items-center gap-2">
                  <Stars n={rating} size={13} />
                  <span className="text-xs text-stone2">{count} {count === 1 ? s.reviews.reviewWord : s.reviews.reviewsWord}</span>
                </div>
              </div>
            </div>
          </div>
        </Reveal>

        {!data && !failed && (
          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6" data-testid="reviews-loading">
            {[0, 1, 2].map((i) => (
              <div key={i} className="h-48 animate-pulse rounded-2xl bg-maroon/5" />
            ))}
          </div>
        )}

        {failed && (
          <Reveal className="mt-14">
            <div
              className="rounded-2xl border border-maroon/10 bg-white p-8 text-center shadow-sm"
              data-testid="reviews-fallback"
            >
              <Stars n={5} size={20} />
              <p className="mt-4 font-display text-2xl font-bold text-ink">
                {s.reviews.fallbackTitle}
              </p>
              <p className="mt-2 text-sm text-stone2">
                {s.reviews.fallbackText}
              </p>
            </div>
          </Reveal>
        )}

        {data && data.reviews?.length > 0 && (
          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="reviews-grid">
            {data.reviews.map((r, i) => (
              <ReviewCard key={i} review={r} i={i} />
            ))}
          </div>
        )}

        <Reveal delay={0.1}>
          <div className="mt-12 flex flex-wrap items-center gap-4">
            <a
              href={data?.googleMapsUri || GBP_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="reviews-read-all-button"
              className="inline-flex items-center gap-2 rounded-full bg-maroon px-7 py-3.5 text-sm font-semibold text-cream transition-colors duration-300 hover:bg-peacock"
            >
              <BookOpen size={16} /> {s.reviews.readAll}
            </a>
            <a
              href={GBP_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="reviews-write-review-button"
              className="inline-flex items-center gap-2 rounded-full border border-maroon/25 px-7 py-3.5 text-sm font-semibold text-maroon transition-colors duration-300 hover:border-saffron hover:text-saffron"
            >
              <MessageSquarePlus size={16} /> {s.reviews.write}
            </a>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
